/**
 * `ConnectorChatRow` — the three-state row for a capability-only chat surface.
 *
 * Sibling of `ConnectorAiRow`, deliberately a separate component rather than a
 * prop on that one: the ladder row's whole shape is "pause + three autonomy
 * rungs, then a capability pair", and a website chat has no autonomy question
 * to ask (the member opened the box — "which inbound does it engage with" has
 * one answer). Threading an `autonomy?: never` through the ladder component
 * would have left every one of its ladder branches live and unreachable for
 * this caller; a 90-line sibling that renders exactly the three real states is
 * the honest shape.
 *
 * Copy comes from the connector's own {@link ConnectorChatDescriptor}, same
 * no-drift rule as the ladder row.
 */

import type { JSX } from "react";
import {
  CONNECTOR_DESCRIPTORS,
  type ConnectorCapability,
} from "@layer/canonical-data/connectors";

/**
 * The three states: the surface's OFF switch, then the capability pair. `off`
 * is not a capability value — it writes a different config key entirely
 * (`studio.webchat_enabled`, the gate the member-api endpoint already
 * enforces) — so the union is modelled explicitly rather than widened out of
 * {@link ConnectorCapability}.
 */
export type ConnectorChatState = "off" | ConnectorCapability;

export function ConnectorChatRow({
  descriptor,
  enabled,
  capability,
  onChange,
}: {
  descriptor: (typeof CONNECTOR_DESCRIPTORS)[number];
  enabled: boolean;
  capability: ConnectorCapability;
  onChange: (next: ConnectorChatState) => void;
}): JSX.Element {
  const chat = descriptor.chat;
  /* c8 ignore next — the caller only renders this row when `chat` is present. */
  if (chat === undefined) return <></>;
  const current: ConnectorChatState = enabled ? capability : "off";
  const options: ReadonlyArray<{
    value: ConnectorChatState;
    label: string;
    detail: string;
  }> = [
    {
      value: "off",
      label: `Off — ${descriptor.label} is paused`,
      detail: descriptor.pauses,
    },
    ...chat.capabilityOptions.map((o) => ({
      value: o.value as ConnectorChatState,
      label: o.label,
      detail: o.detail,
    })),
  ];
  const rowId = `connector-chat-${descriptor.key}`;
  return (
    <div
      className="connector-row"
      style={{ padding: "8px 0", borderTop: "1px solid var(--c-line-94-c)" }}
    >
      <div style={{ fontWeight: 600 }}>
        {descriptor.label}
        {!enabled && (
          <span
            className="tag tag-danger"
            style={{ marginLeft: 8, fontSize: "0.8em" }}
          >
            paused
          </span>
        )}
        {enabled && capability === "converse" && (
          <span className="tag" style={{ marginLeft: 8, fontSize: "0.8em" }}>
            answer only
          </span>
        )}
      </div>
      <div style={{ fontSize: "0.85em", color: "var(--c-ink-41)", margin: "2px 0 6px" }}>
        Members who open this chat have chosen to talk to the assistant, so
        there is no “only answer some of them” setting — just whether it is on,
        and what it may do.
      </div>
      {options.map((opt) => (
        <label
          key={opt.value}
          htmlFor={`${rowId}-${opt.value}`}
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 8,
            padding: "5px 0",
            cursor: "pointer",
          }}
        >
          <input
            type="radio"
            id={`${rowId}-${opt.value}`}
            name={rowId}
            value={opt.value}
            checked={current === opt.value}
            onChange={() => onChange(opt.value)}
            style={{ marginTop: 3 }}
          />
          <span style={{ flex: "1 1 auto" }}>
            <span style={{ fontWeight: 600 }}>{opt.label}</span>
            <span
              style={{
                display: "block",
                fontSize: "0.85em",
                color: "var(--c-ink-41)",
                marginTop: 2,
              }}
            >
              {opt.detail}
            </span>
          </span>
        </label>
      ))}
      <div style={{ fontSize: "0.8em", color: "var(--c-ink-41)", marginTop: 4 }}>
        {chat.note}
      </div>
    </div>
  );
}
